import { useEffect, useState } from "react";
import { Check, ArrowRight } from "lucide-react";
import "./Home.css";
import "./Marketing.css";
import { InviteForm, Bubble, SiteNav, SiteFooter, useSeo } from "./_site";

type Feature = {
  key: string; label: string; title: string; blurb: string; note: string | null;
  example: ["me" | "remy", string][];
};
type FeatureList = { live: Feature[]; soon: Feature[]; promises: string[] };

const API = process.env.REACT_APP_BACKEND_URL;
const FALLBACK_PROMISES = [
  "Remy asks before anything that affects someone else",
  "You choose what to connect, and you can disconnect anytime",
];

export default function WhatRemyCanDo() {
  const [data, setData] = useState<FeatureList | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let live = true;
    fetch(`${API}/api/features`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("unavailable"))))
      .then((d) => { if (live) setData(d); })
      .catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, []);

  useSeo(
    "What Remy Can Do | Hello Remy",
    "See what Remy does today: reminders, your calendar, photos, email and calendar watch, and the people in your life. All by text."
  );

  return (
    <div className="remy-home" data-testid="what-remy-can-do-page">
      <SiteNav />

      {/* HERO */}
      <header className="mk-phero" id="top">
        <div className="rh-wrap mk-phero__grid">
          <div className="rh-hero__copy mk-hero-copy">
            <p className="rh-eyebrow">What Remy can do</p>
            <h1 className="rh-hdtext" data-testid="what-hero-heading">Less to remember. Less to chase.</h1>
            <p className="rh-body">
              Remy is your personal AI agent. No app to learn. You just text.
            </p>
            <div className="mk-cta-row">
              <a href="#invite" className="rh-btn" data-testid="what-hero-cta">Request an invite <ArrowRight size={18} /></a>
            </div>
          </div>
          <div className="mk-convo" data-testid="what-hero-convo">
            <span className="mk-convo__cap">Messages · Remy</span>
            <div className="rh-bubbles">
              <Bubble who="me">What should I keep an eye on this week?</Bubble>
              <Bubble who="remy">Your invoice is due Thursday, and Mom's birthday is Saturday. I'll remind you.</Bubble>
            </div>
          </div>
        </div>
      </header>

      {/* CAPABILITIES — rendered from the product's own feature list (GET /api/features), so
          this page updates in the same release as each feature. */}
      <section className="rh-section">
        <div className="rh-wrap">
          <div className="mk-head mk-head--center">
            <p className="rh-kicker">Today</p>
            <h2 className="mk-h2">What Remy does right now</h2>
          </div>
          {!data && (
            <p className="rh-lead" style={{ textAlign: "center" }} data-testid="what-loading">
              {failed ? "Text Remy \"what can you do?\" for the current list." : "Loading…"}
            </p>
          )}
          <div className="mk-caps" data-testid="what-live-list">
            {data?.live.map((f, i) => (
              <div className={`mk-cap-row${i % 2 ? " mk-cap-row--rev" : ""}`} key={f.key}
                   data-testid={`what-feature-${f.key}`}>
                <div className="mk-cap-row__copy">
                  <span className="mk-label">{f.label}</span>
                  <h2 className="mk-h2" style={{ marginTop: 12 }}>{f.title}</h2>
                  <p className="rh-lead">{f.blurb}</p>
                  {f.note && <p className="mk-note">{f.note}</p>}
                </div>
                <div className="mk-cap-row__media">
                  <div className="rh-bubbles">
                    {f.example.map(([who, text], n) => (
                      <Bubble who={who} key={n}>{text}</Bubble>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMING SOON */}
      {data && data.soon.length > 0 && (
        <section className="rh-section mk-section--tight mk-future" data-testid="what-soon-list">
          <div className="rh-wrap">
            <div className="mk-head mk-head--center">
              <p className="rh-kicker">Coming soon</p>
              <h2 className="mk-h2">Remy is just getting started.</h2>
              {data.soon.map((f) => (
                <p key={f.key}><strong>{f.title}.</strong> {f.blurb}{f.note ? ` ${f.note}` : ""}</p>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* TRUST STRIP */}
      <section className="rh-section mk-section--tight mk-trust">
        <div className="rh-wrap">
          <div className="mk-head mk-head--center">
            <p className="rh-kicker">Built around your permission</p>
            <h2 className="mk-h2 mk-h2--red">On your terms.</h2>
          </div>
          <div className="mk-strip">
            {(data?.promises ?? FALLBACK_PROMISES).map((line) => (
              <div className="mk-strip__item" key={line}><Check size={20} /><span>{line}</span></div>
            ))}
          </div>
        </div>
      </section>

      {/* BOTTOM CTA */}
      <section className="rh-section rh-private" id="invite">
        <div className="rh-wrap rh-private__grid">
          <div className="rh-private__copy">
            <p className="rh-kicker">Private access</p>
            <h2 className="rh-want">Want in?</h2>
            <p className="rh-private__sub">We're opening Remy slowly.</p>
            <p className="rh-lead">
              A small group gets access first so we can make the experience feel right before opening it up
              more broadly.
            </p>
          </div>
          <div className="rh-private__card">
            <InviteForm idPrefix="what" variant="card" source="what_remy_can_do" />
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
